import React, { useCallback, useMemo, useRef, useState } from 'react';
import tw, { TwStyle } from 'twin.macro';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';

import { TIER_MAPPER } from './ContextualTierBadge';
import { SortableHead } from './Table';
import { useMyLibraryContext } from '@insights_module/context/MyLibraryContext';
import { Pagination } from '@components/Pagination';
import {
  Audio,
  Group,
  AudioContextual,
  GroupContextual,
  AudioLinkedNamedEntity,
} from '@insights_module/types/graphql/Schema';
import {
  useLibraryTableColumns,
  LIBRARY_TABLE_COLUMN_KEYS,
} from '@insights_module/hooks/useLibraryTableColumns';

import {
  TableType,
  TableRowType,
  AudiosSortKeyType,
  GroupsSortKeyType,
  PaginationDataType,
  TableViewType,
} from '@insights_module/types/LibraryTable';

import { Loader } from '@components/Loader';
import { FiltersModal } from '@insights_module/components/FiltersModal';
import { LibraryTableHeader } from '@insights_module/components//LibraryTableHeader';
import { validateArtworkUrl } from '@insights_module/utils/showsUtils';
import { GARM_CATEGORIES } from '@insights_module/constants/garm';
import { CategoryType, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { ThreeDots } from '@icons/ThreeDots';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { convertSecondsToHMS } from '@utils/dateUtils';
import { useLibraryTableData } from '@insights_module/hooks/useLibraryTableData';
import { BrandSafetyRiskBadge } from '@insights_module/components/BrandSafetyRiskBadge';
import { TabSwitch } from '@insights_module/components/TabSwitch';
import { TableIcon } from '@icons/TableIcon';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { Switch } from '@components/Switch';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { SCREENS, useMediaQueries } from '@app/hooks/useMediaQueries';
import { ExportSelect } from '@insights_module/components/shows_screen/ExportsSelect';
import { NoTableData } from '@insights_module/components/NoTableData';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';

const Container = tw.div`w-full bg-neutral-background flex flex-col h-full`;
const StyledTable = tw.table`flex flex-col w-full h-full grow overflow-hidden`;
const StyledTHead = tw.thead`border-b border-solid border-gray-1 flex gap-[2px] flex-col`;
const StyledTBody = tw.tbody`border-b border-solid border-gray-1 w-full max-h-full overflow-y-scroll flex flex-col gap-[2px] grow relative`;
const StyledTFoot = tw.tfoot`sticky w-full bottom-0 right-0 px-[12px] py-[6px] bg-neutral-background flex flex-col items-center justify-end lg:justify-between lg:flex-row`;
const StyledTR = tw.tr`flex w-full relative gap-[2px]`;
const StyledTH = tw.th`text-[11px] text-text-and-icon-muted leading-normal font-semibold px-[4px] py-[3.5px] border-r border-solid border-gray-1 last:border-r-0 flex-1 truncate`;
const StyledTD = tw.td`truncate relative text-[13px] text-text-and-icon-muted flex-1 flex items-center`;
const PaginationWrapper = tw.tr`w-full flex flex-row items-center justify-end`;

const StyledBrandSafetyCell = tw.div`flex rounded-[4px] h-full w-full py-[14px] bg-gray-2 justify-center items-center`;
const DetailsContainer = tw.div`flex items-center justify-start gap-[8px] h-full p-[14px] pl-[24px] w-full`;
const Thumbnail = tw.img`w-[32px] h-[32px] min-w-[32px] min-h-[32px] rounded-[2px]`;
const Title = tw.div`text-left text-[13px] font-medium text-text-and-icon-muted leading-[130%] max-w-full truncate cursor-pointer hover:underline`;

const ContextualCellWrapper = tw.div`flex items-center justify-start flex-wrap gap-[6px]`;
const BaseLabelWrapper = tw.div`border border-solid border-sec-2 rounded-[6px] py-[4px] px-[12px] bg-neutral-background text-[11px] text-text-and-icon flex items-center justify-center gap-[4px]`;
const StyledContextualLabel = tw(
  BaseLabelWrapper
)`pl-[3px] pr-[6px] flex items-center justify-start gap-[6px]`;
const ContextualScore = tw.span`h-[20px] w-[20px] flex items-center justify-center text-accent-1 text-[11px] bg-sec-4 rounded-[4px]`;

const ColumnsFiltersWrapper = tw.div`relative`;
const ColumnFilterItem = tw.button`w-full flex items-center justify-between text-text-and-icon text-[14px] leading-[140%] font-semibold py-[6px] px-[8px] [&>span]:block [&>span]:text-left [&>span]:w-full [&>span]:truncate`;
const ColumnsFiltersButton = tw.button`text-[11px] font-normal text-text-and-icon flex items-center justify-between transition-all duration-200 border border-solid border-gray-1 rounded-[4px] cursor-pointer px-[8px] h-[26px]`;
const ColumnsFiltersContainer = tw.div`absolute top-[30px] right-0 bg-neutral-background border border-solid border-gray-1 rounded-[6px] shadow-modal p-[4px] w-[276px]`;

const FallbackWrapper = tw.div`m-auto w-full`;

export const DetailsCell = (
  data: Audio & Group,
  selectedView: TableViewType,
  handleSelectItem: ((id: string) => void) | null
) => {
  const id = data?.id;
  const title = data?.name || data?.title;
  const imageUrl = validateArtworkUrl(data?.thumbnail_url || data?.groups?.[0]?.thumbnail_url);

  return (
    <DetailsContainer css={[selectedView === 'brand_safety_garm' && tw`bg-gray-2`]}>
      <Thumbnail src={imageUrl} alt={`${title} cover`} />
      <Title
        css={[handleSelectItem ? tw`cursor-pointer` : tw`cursor-default hover:no-underline`]}
        onClick={() => handleSelectItem && handleSelectItem(id)}
        title={title}>
        {title}
      </Title>
    </DetailsContainer>
  );
};

export const BrandSafetyCell = (data: string, showRiskBadge: boolean): JSX.Element | undefined => {
  if (data) {
    return (
      <StyledBrandSafetyCell css={[BRAND_SAFETY_RISK_LEVEL_COLOR_MAPPER[data?.toLowerCase()]]}>
        {showRiskBadge && <BrandSafetyRiskBadge level={data?.toLowerCase() as GarmRiskLevelType} />}
      </StyledBrandSafetyCell>
    );
  }
};

const CONTEXTUAL_LABELS_MAX_LENGTH = 5;

export const ContextualDataCell = (
  data: AudioContextual[] | GroupContextual[],
  id?: string,
  idsForShowAllLabels?: string[],
  setIdsForShowAllLabels?: (ids: string[]) => void
): JSX.Element | undefined => {
  const max_labels_count = idsForShowAllLabels?.find(
    (idForShowAllLabels) => idForShowAllLabels === id
  )
    ? data?.length
    : CONTEXTUAL_LABELS_MAX_LENGTH;
  const hiddenLabelsCount = data?.length - max_labels_count;
  const showLessLabels = idsForShowAllLabels?.includes(id as string);

  const handleToggleShowAllLabels = () => {
    if (!id || !idsForShowAllLabels || !setIdsForShowAllLabels) return;
    if (showLessLabels) {
      setIdsForShowAllLabels(
        idsForShowAllLabels.filter((idForShowAllLabels) => idForShowAllLabels !== id)
      );
    } else {
      setIdsForShowAllLabels([...idsForShowAllLabels, id]);
    }
  };

  return (
    <ContextualCellWrapper>
      {!data?.length || !Array.isArray(data) ? (
        <BaseLabelWrapper>No contextual data detected</BaseLabelWrapper>
      ) : (
        <>
          {data?.slice(0, max_labels_count)?.map((item) => {
            const tier = 'tier' in item ? item.tier : item.taxonomy[0].tier;
            const displayName = 'display_name' in item ? item.display_name : item.name;

            return (
              <StyledContextualLabel key={item.id}>
                <ContextualScore>{TIER_MAPPER[tier as keyof typeof TIER_MAPPER]}</ContextualScore>
                <span>{displayName}</span>
              </StyledContextualLabel>
            );
          })}
          {(data?.length > max_labels_count || showLessLabels) && (
            <BaseLabelWrapper tw='cursor-pointer' onClick={handleToggleShowAllLabels}>
              <ThreeDots tw='fill-gray-1 stroke-gray-1' />
              {showLessLabels ? 'See less' : `${hiddenLabelsCount} more`}
            </BaseLabelWrapper>
          )}
        </>
      )}
    </ContextualCellWrapper>
  );
};

const ENTITIES_LABELS_MAX_LENGTH = 5;

export const EntitiesDataCell = (
  data: AudioLinkedNamedEntity[],
  id?: string,
  idsForShowAllLabels?: string[],
  setIdsForShowAllLabels?: (ids: string[]) => void
): JSX.Element | undefined => {
  const max_labels_count = idsForShowAllLabels?.find(
    (idForShowAllLabels) => idForShowAllLabels === id
  )
    ? data?.length
    : ENTITIES_LABELS_MAX_LENGTH;
  const hiddenLabelsCount = data?.length - max_labels_count;
  const showLessLabels = idsForShowAllLabels?.includes(id as string);

  const handleToggleShowAllLabels = () => {
    if (!id || !idsForShowAllLabels || !setIdsForShowAllLabels) return;
    if (showLessLabels) {
      setIdsForShowAllLabels(
        idsForShowAllLabels.filter((idForShowAllLabels) => idForShowAllLabels !== id)
      );
    } else {
      setIdsForShowAllLabels([...idsForShowAllLabels, id]);
    }
  };

  return (
    <ContextualCellWrapper>
      {!data?.length || !Array.isArray(data) ? (
        <BaseLabelWrapper>No entities data detected</BaseLabelWrapper>
      ) : (
        <>
          {data?.slice(0, max_labels_count)?.map((item) => {
            const displayName = item.value;

            return (
              <StyledContextualLabel key={item.id}>
                <span>{displayName}</span>
              </StyledContextualLabel>
            );
          })}
          {(data?.length > max_labels_count || showLessLabels) && (
            <BaseLabelWrapper tw='cursor-pointer' onClick={handleToggleShowAllLabels}>
              <ThreeDots tw='fill-gray-1 stroke-gray-1' />
              {showLessLabels ? 'See less' : `${hiddenLabelsCount} more`}
            </BaseLabelWrapper>
          )}
        </>
      )}
    </ContextualCellWrapper>
  );
};

export const BaseCell = (data: string): JSX.Element | undefined => {
  if (data) {
    return <div tw='px-[4px]'>{data}</div>;
  }
};

const GROUPS_SORTABLE_KEY = {
  NAME: 'name' as GroupsSortKeyType,
  AUDIO_COUNT: 'audio_count' as GroupsSortKeyType,
  CREATED_AT: 'created_at' as GroupsSortKeyType,
  MOST_RECENTLY_PUBLISHED_AUDIO_DATE: 'most_recently_published_audio_date' as GroupsSortKeyType,
};

export const AUDIOS_SORTABLE_KEY = {
  TITLE: 'title' as AudiosSortKeyType,
  PUBLISHED_DATE: 'published_date' as AudiosSortKeyType,
  DURATION: 'duration' as AudiosSortKeyType,
  CREATED_AT: 'created_at' as AudiosSortKeyType,
  UPDATED_AT: 'updated_at' as AudiosSortKeyType,
};

const BRAND_SAFETY_RISK_LEVEL_COLOR_MAPPER = {
  unrelated: tw`bg-gray-2` as TwStyle,
  low: tw`bg-garm-low-bg` as TwStyle,
  medium: tw`bg-garm-medium-bg` as TwStyle,
  high: tw`bg-garm-high-bg` as TwStyle,
  floor: tw`bg-garm-floor-dark` as TwStyle,
  'likely unrelated': tw`bg-garm-low-bg` as TwStyle,
  'likely related': tw`bg-garm-medium-bg` as TwStyle,
  related: tw`bg-garm-floor-dark` as TwStyle,
} as { [key: string]: TwStyle };

const GARM_LEGEND_COLOR_MAPPER = omit(BRAND_SAFETY_RISK_LEVEL_COLOR_MAPPER, [
  'likely unrelated',
  'likely related',
  'related',
]);

const TABLE_TYPE = {
  AUDIOS: 'audios' as TableType,
  GROUPS: 'groups' as TableType,
};

const groupsSortableColumns = Object.values(GROUPS_SORTABLE_KEY);
const audiosSortableColumns = Object.values(AUDIOS_SORTABLE_KEY);

const BrandSafetyLegendItem = ({ risk, value }: { risk: string; value: TwStyle }) => (
  <div tw='flex gap-[4px] items-center rounded whitespace-nowrap'>
    <div
      tw='w-[12px] h-[12px] rounded-[2px]'
      css={[value, risk === 'unrelated' && tw`border border-gray-1`]}
    />
    <div tw='capitalize text-[12px] text-text-and-icon font-medium'>{risk}</div>
  </div>
);

export const BrandSafetyLegend = ({ type }: { type: TableViewType }) => {
  const { t } = useTranslation();

  if (type !== 'brand_safety_garm') return null;

  return (
    <tr tw='flex gap-[8px] items-center w-full'>
      <td tw='flex gap-[8px]'>
        <div tw='text-[14px] text-text-and-icon font-medium'>
          {t('insights.my_library_page.table.brand_safety_legend_title')}
        </div>
        {Object.entries(GARM_LEGEND_COLOR_MAPPER).map(([key, value]) => (
          <BrandSafetyLegendItem key={key} risk={key} value={value} />
        ))}
      </td>
    </tr>
  );
};

export const LibraryTable = () => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQueries(SCREENS.SM);
  const {
    tableType,
    selectedView,
    setSelectedView,
    audiosTableSort,
    groupsTableSort,
    setAudiosTableSort,
    setGroupsTableSort,
    audiosColumns,
    groupsColumns,
    colorBlindMode,
  } = useMyLibraryContext();
  const { setSelectedEpisode, setSelectedShow } = useInsightsContext();
  const isGroupsTable = useMemo(() => tableType === TABLE_TYPE.GROUPS, [tableType]);
  const { columns } = useLibraryTableColumns(selectedView, tableType);
  const [idsForShowAllLabels, setIdsForShowAllLabels] = useState<string[]>([]);
  const { searchTerm } = useInsightsParams();

  const {
    audiosData,
    isAudiosLoading,
    isFetchingAudios,
    isPrefetchingAudios,
    audiosCurrentPage,
    audiosPaginationRange,
    audiosTotalPages,
    audiosGoToNextPage,
    audiosGoToPreviousPage,
    audiosGoToPage,
    groupsData,
    isGroupsLoading,
    isFetchingGroups,
    isPrefetchingGroups,
    groupsCurrentPage,
    groupsPaginationRange,
    groupsTotalPages,
    groupsGoToNextPage,
    groupsGoToPreviousPage,
    groupsGoToPage,
  } = useLibraryTableData();

  const paginationData: PaginationDataType = useMemo(
    () => ({
      currentPage: isGroupsTable ? groupsCurrentPage : audiosCurrentPage,
      paginationRange: isGroupsTable ? groupsPaginationRange : audiosPaginationRange,
      total: isGroupsTable ? groupsTotalPages : audiosTotalPages,
    }),
    [
      audiosCurrentPage,
      audiosPaginationRange,
      audiosTotalPages,
      groupsCurrentPage,
      groupsPaginationRange,
      groupsTotalPages,
      isGroupsTable,
    ]
  );

  const handleGoToNextPage = isGroupsTable ? groupsGoToNextPage : audiosGoToNextPage;

  const handleGoToPreviousPage = isGroupsTable ? groupsGoToPreviousPage : audiosGoToPreviousPage;

  const handleGoToPage = isGroupsTable ? groupsGoToPage : audiosGoToPage;

  const selectedViewAudiosData = useMemo(() => {
    if (selectedView === 'info') {
      return audiosData?.map((episode) => ({
        details: episode.details as Audio,
        published_date: new Date(episode.published_date).toLocaleDateString() || '-/-',
        duration: episode?.details?.duration
          ? convertSecondsToHMS(episode.details.duration)
          : '-/-',
        author: episode.details?.groups?.[0]?.podcast_metadata?.author || 'No author data',
        podcast_name: episode.details?.groups?.[0]?.name || 'No podcast name',
        status: 'Published', // TODO: Missing data
      }));
    }
    if (selectedView === 'contextual') {
      return audiosData?.map((episode) => ({
        details: episode.details as Audio,
        published_date: new Date(episode.published_date).toLocaleDateString() || '-/-',
        contextual: episode.contextual,
      }));
    }

    if (selectedView === 'brand_safety_garm') {
      return audiosData?.map((episode) => ({
        details: episode.details as Audio,
        published_date: new Date(episode.published_date).toLocaleDateString() || '-/-',
        ...episode.garm,
      }));
    }
  }, [selectedView, audiosData]);

  const selectedViewGroupsData = useMemo(() => {
    if (selectedView === 'info') {
      return groupsData?.map((group) => ({
        details: group,
        author: group?.name || 'No author data',
        audio_count: group.processed_audios_details?.processed_audio_count,
        type: group?.podcast_metadata?.podcast_type || '-',
        most_recently_published_audio_date: group?.most_recently_published_audio_date
          ? new Date(group?.most_recently_published_audio_date).toLocaleDateString()
          : '-/-',
      }));
    }
    if (selectedView === 'brand_safety_garm') {
      return groupsData?.map((group) => ({
        details: group,
        most_recently_published_audio_date: group?.most_recently_published_audio_date
          ? new Date(group?.most_recently_published_audio_date).toLocaleDateString()
          : '-/-',
        ...group.garm,
      }));
    }

    if (selectedView === 'contextual') {
      return groupsData?.map((group) => ({
        details: group,
        most_recently_published_audio_date: group?.most_recently_published_audio_date
          ? new Date(group?.most_recently_published_audio_date).toLocaleDateString()
          : '-/-',
        contextual: group.contextual,
      }));
    }
  }, [selectedView, groupsData]);

  const getDataByTableType = useCallback(
    (tableType: TableType) =>
      tableType === TABLE_TYPE.GROUPS ? selectedViewGroupsData : selectedViewAudiosData,
    [selectedView, audiosData, groupsData, tableType, audiosCurrentPage, groupsCurrentPage]
  );

  const tableData = getDataByTableType(tableType) as TableRowType[];

  const handleGroupsTableSort = useCallback(
    (key: GroupsSortKeyType) => {
      if (!isGroupsTable || !groupsSortableColumns.includes(key)) return;

      if (groupsTableSort.sortField === key) {
        setGroupsTableSort({
          ...groupsTableSort,
          sortOrder: groupsTableSort?.sortOrder === 'asc' ? 'desc' : 'asc',
        });

        return;
      }

      setGroupsTableSort({
        ...groupsTableSort,
        sortField: key,
      });
    },
    [isGroupsTable, groupsTableSort, selectedView]
  );

  const handleAudiosTableSort = useCallback(
    (key: AudiosSortKeyType) => {
      if (isGroupsTable || !audiosSortableColumns.includes(key)) return;

      if (audiosTableSort.sortField === key) {
        setAudiosTableSort({
          ...audiosTableSort,
          sortField: key,
          sortOrder: audiosTableSort?.sortOrder === 'asc' ? 'desc' : 'asc',
        });

        return;
      }
      setAudiosTableSort({
        ...audiosTableSort,
        sortField: key,
      });
    },
    [isGroupsTable, audiosTableSort, selectedView]
  );
  const getTableColumns = useCallback(
    (
      key: string
    ):
      | {
          key: string;
          align: 'left' | 'right' | 'center';
          header: JSX.Element;
          cellRender: (
            data: string | number | Audio | Group | TableRowType | AudioContextual[],
            id?: string,
            idsForShowAllLabels?: string[],
            setIdsForShowAllLabels?: (ids: string[]) => void
          ) => JSX.Element | undefined;
        }
      | undefined => {
      const handleSort = (key: GroupsSortKeyType | AudiosSortKeyType) =>
        isGroupsTable
          ? handleGroupsTableSort(key as GroupsSortKeyType)
          : handleAudiosTableSort(key as AudiosSortKeyType);

      // BRAND_SAFETY COLUMNS GARM
      if (Object.values(GARM_CATEGORIES).includes(key as CategoryType)) {
        return {
          key: key,
          align: 'left',
          header: <>{t(`insights.my_library_page.table.${key}`)}</>,
          cellRender: (data) => BrandSafetyCell(data as string, colorBlindMode),
        };
      }

      switch (key) {
        // SHARED COLUMNS
        case LIBRARY_TABLE_COLUMN_KEYS.DETAILS:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
            align: 'left',
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={
                  isGroupsTable
                    ? groupsTableSort.sortField === GROUPS_SORTABLE_KEY.NAME
                    : audiosTableSort.sortField === AUDIOS_SORTABLE_KEY.TITLE
                }
                tooltip={`Sort by ${
                  isGroupsTable
                    ? t(`insights.my_library_page.table.${GROUPS_SORTABLE_KEY.NAME}`)
                    : t(`insights.my_library_page.table.${AUDIOS_SORTABLE_KEY.TITLE}`)
                }`}
                order={isGroupsTable ? groupsTableSort.sortOrder : audiosTableSort.sortOrder}
                onClick={() =>
                  handleSort(isGroupsTable ? GROUPS_SORTABLE_KEY.NAME : AUDIOS_SORTABLE_KEY.TITLE)
                }
              />
            ),
            cellRender: (data) =>
              DetailsCell(data as Audio & Group, selectedView, handleSelectItem),
          };

        case LIBRARY_TABLE_COLUMN_KEYS.CONTEXTUAL:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.CONTEXTUAL,
            align: 'left',
            header: <>{t(`insights.my_library_page.table.${key}`)}</>,
            cellRender: (data, id, idsForShowAllLabels, setIdsForShowAllLabels) =>
              ContextualDataCell(
                data as AudioContextual[] | GroupContextual[],
                id,
                idsForShowAllLabels,
                setIdsForShowAllLabels
              ),
          };

        case LIBRARY_TABLE_COLUMN_KEYS.AUTHOR:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.AUTHOR,
            header: <>{t(`insights.my_library_page.table.${key}`)}</>,
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };

        // EPISODES COLUMNS
        case LIBRARY_TABLE_COLUMN_KEYS.DURATION:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.DURATION,
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={
                  isGroupsTable
                    ? groupsTableSort.sortField === LIBRARY_TABLE_COLUMN_KEYS.DURATION
                    : audiosTableSort.sortField === LIBRARY_TABLE_COLUMN_KEYS.DURATION
                }
                tooltip={`Sort by ${t(`insights.my_library_page.table.${LIBRARY_TABLE_COLUMN_KEYS.DURATION}`)}`}
                order={isGroupsTable ? groupsTableSort.sortOrder : audiosTableSort.sortOrder}
                onClick={() => handleSort(LIBRARY_TABLE_COLUMN_KEYS.DURATION as AudiosSortKeyType)}
              />
            ),
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };

        case LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={audiosTableSort.sortField === LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE}
                tooltip={`Sort by ${t(`insights.my_library_page.table.${LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE}`)}`}
                order={isGroupsTable ? groupsTableSort.sortOrder : audiosTableSort.sortOrder}
                onClick={() =>
                  handleSort(LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE as AudiosSortKeyType)
                }
              />
            ),
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };

        case LIBRARY_TABLE_COLUMN_KEYS.PODCAST_NAME:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.PODCAST_NAME,
            header: <>{t(`insights.my_library_page.table.${key}`)}</>,
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };

        // Temporarily commented column, because we do not have this information about the audio.
        // case LIBRARY_TABLE_COLUMN_KEYS.STATUS:
        //   return {
        //     key: LIBRARY_TABLE_COLUMN_KEYS.STATUS,
        //     header: <>{t(`insights.my_library_page.table.${key}`)}</>,
        //     align: 'left',
        //     cellRender: (data) => BaseCell(data as string),
        //   };

        // GROUPS COLUMNS
        case LIBRARY_TABLE_COLUMN_KEYS.TYPE:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.TYPE,
            header: <>{t(`insights.my_library_page.table.${key}`)}</>,
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };

        case LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE,
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={
                  groupsTableSort.sortField ===
                  LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE
                }
                tooltip={`Sort by ${t(`insights.my_library_page.table.${LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE}`)}`}
                order={groupsTableSort.sortOrder}
                onClick={() =>
                  handleSort(
                    LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE as AudiosSortKeyType
                  )
                }
              />
            ),
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };

        case LIBRARY_TABLE_COLUMN_KEYS.AUDIO_COUNT:
          return {
            key: LIBRARY_TABLE_COLUMN_KEYS.AUDIO_COUNT,
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={groupsTableSort.sortField === LIBRARY_TABLE_COLUMN_KEYS.AUDIO_COUNT}
                tooltip={`Sort by ${t(`insights.my_library_page.table.${LIBRARY_TABLE_COLUMN_KEYS.AUDIO_COUNT}`)}`}
                order={groupsTableSort.sortOrder}
                onClick={() =>
                  handleSort(LIBRARY_TABLE_COLUMN_KEYS.AUDIO_COUNT as AudiosSortKeyType)
                }
              />
            ),
            align: 'left',
            cellRender: (data) => BaseCell(data as string),
          };
      }
    },
    [
      isGroupsTable,
      selectedView,
      audiosTableSort,
      groupsTableSort,
      setAudiosTableSort,
      setGroupsTableSort,
      handleGroupsTableSort,
      handleAudiosTableSort,
    ]
  );
  const tableColumns = useMemo(() => {
    if (tableType === 'groups') {
      return columns
        .map((col) => getTableColumns(col?.key))
        .filter((item) => item && groupsColumns.includes(item.key));
    }
    return columns
      .map((col) => getTableColumns(col?.key))
      .filter((item) => item && audiosColumns.includes(item.key));
  }, [columns, tableType, groupsColumns, audiosColumns, selectedView, getTableColumns]);

  const handleSelectItem = (id: string) => {
    isGroupsTable ? setSelectedShow(id) : setSelectedEpisode(id);
  };

  const fallback = useMemo<React.ReactNode>(() => {
    const hasSearch = searchTerm?.length;

    if (tableType === 'groups') {
      return hasSearch ? <NoTableData.ShowSearchResult /> : <NoTableData.Show />;
    }

    return hasSearch ? <NoTableData.EpisodeSearchResult /> : <NoTableData.Episode />;
  }, [tableType, searchTerm, tableData]);

  const isLoading = useMemo<boolean>(
    () =>
      ((isAudiosLoading || isFetchingAudios) && tableType === 'audios') ||
      ((isGroupsLoading || isFetchingGroups) && tableType === 'groups'),
    [tableType, isAudiosLoading, isFetchingAudios, isGroupsLoading, isFetchingGroups]
  );

  return (
    <Container>
      <LibraryTableHeader />
      <FiltersModal />
      <StyledTable>
        <StyledTHead>
          <StyledTR tw='z-30'>
            <StyledTH tw='flex justify-between items-center px-[24px] overflow-visible'>
              <TabSwitch
                tabs={[
                  {
                    id: 'contextual',
                    label: t('insights.my_library_page.view.contextual') as string,
                  },
                  {
                    id: 'brand_safety_garm',
                    label: isMobile
                      ? (t('insights.my_library_page.view.garm_short') as string)
                      : (t('insights.my_library_page.view.garm') as string),
                  },

                  { id: 'info', label: t('insights.my_library_page.view.info') as string },
                ]}
                selectedTab={selectedView}
                setSelectedTab={(val) => setSelectedView(val as TableViewType)}
              />
              <div tw='flex gap-[16px]'>
                <ExportSelect />
                <ColumnsFilterMenu />
              </div>
            </StyledTH>
          </StyledTR>
          <StyledTR>
            {tableColumns?.map((column, index) => (
              <StyledTH
                title={t(`insights.my_library_page.table.${column?.key}`)}
                key={index}
                style={{ textAlign: column?.align as 'left' | 'right' }}
                css={[
                  column?.key === LIBRARY_TABLE_COLUMN_KEYS.DETAILS &&
                    tw`min-w-[247px] max-w-[246px] pl-[24px]`,
                  column?.key === LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE &&
                    tw`max-w-[120px] min-w-[120px]`,
                  column?.key === LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE &&
                    tw`max-w-[172px] min-w-[122px]`,
                  column?.key === LIBRARY_TABLE_COLUMN_KEYS.DURATION && tw`max-w-[100px]`,
                ]}>
                {column?.header}
              </StyledTH>
            ))}
            <StyledTH tw={'min-w-[100px] max-w-[100px]'}></StyledTH>
          </StyledTR>
        </StyledTHead>
        <StyledTBody>
          {isLoading && (
            <StyledTR tw='absolute flex justify-center items-center w-full h-full z-10 bg-neutral-background'>
              <StyledTD>
                <Loader size='md' theme='dark' />
              </StyledTD>
            </StyledTR>
          )}
          {!isLoading && !tableData?.length ? (
            <StyledTR>
              <StyledTD colSpan={columns.length}>
                <FallbackWrapper>{fallback}</FallbackWrapper>
              </StyledTD>
            </StyledTR>
          ) : (
            tableData?.map((row, index: number) => (
              <StyledTR
                key={index}
                css={[
                  (selectedView === 'info' || selectedView === 'contextual') &&
                    tw`odd:bg-gray-2 even:bg-neutral-background`,
                ]}>
                {tableColumns.map((col, index) => (
                  <StyledTD
                    key={index}
                    css={[
                      col?.key === LIBRARY_TABLE_COLUMN_KEYS.DETAILS &&
                        tw`min-w-[246px] max-w-[246px]`,
                      col?.key === LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE &&
                        tw`max-w-[120px] min-w-[120px]`,
                      col?.key === LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE &&
                        selectedView === 'brand_safety_garm' &&
                        tw`bg-gray-2 rounded my-[1px]`,
                      col?.key === LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE &&
                        tw`max-w-[172px] min-w-[122px]`,
                      col?.key === LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE &&
                        selectedView === 'brand_safety_garm' &&
                        tw`bg-gray-2 rounded my-[1px]`,
                      col?.key === LIBRARY_TABLE_COLUMN_KEYS.DURATION && tw`max-w-[100px]`,
                    ]}>
                    {col?.cellRender
                      ? col?.cellRender(
                          row?.[col?.key as keyof TableRowType],
                          row?.details?.id,
                          idsForShowAllLabels,
                          setIdsForShowAllLabels
                        )
                      : String(row?.[col?.key as keyof TableRowType])}
                  </StyledTD>
                ))}
                <StyledTD tw={'min-w-[100px] max-w-[100px] justify-center'}>
                  <div
                    tw='flex justify-center items-center w-full h-full rounded'
                    css={[selectedView === 'brand_safety_garm' && tw`bg-gray-2`]}>
                    <button
                      onClick={() => handleSelectItem(row.details.id)}
                      tw=' bg-gray-3 rounded-[4px] px-[12px] py-[4px] text-[12px] text-text-and-icon-muted'>
                      Open
                    </button>
                  </div>
                </StyledTD>
              </StyledTR>
            ))
          )}
        </StyledTBody>
        <StyledTFoot>
          <BrandSafetyLegend type={selectedView} />

          <PaginationWrapper>
            <td tw='flex items-end gap-[16px]'>
              {(isPrefetchingAudios || isPrefetchingGroups) && <Loader size='sm' theme='dark' />}

              <Pagination
                currentPage={paginationData.currentPage}
                totalPages={paginationData.total}
                paginationRange={paginationData.paginationRange}
                onPageChange={(num) => handleGoToPage(num)}
                onNext={handleGoToNextPage}
                onPrevious={handleGoToPreviousPage}
              />
            </td>
          </PaginationWrapper>
        </StyledTFoot>
      </StyledTable>
    </Container>
  );
};

const ColumnsFilterMenu = () => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const dropdownRef = useRef(null);
  const {
    selectedView,
    tableType,
    audiosColumns,
    groupsColumns,
    setAudiosColumn,
    setGroupsColumn,
    colorBlindMode,
    setColorBlindMode,
  } = useMyLibraryContext();
  const { columns } = useLibraryTableColumns(selectedView, tableType);

  useOnClickOutside(dropdownRef, () => setIsExpanded(false));

  const filteredColumns = useMemo(() => {
    return columns
      .filter((item) => {
        if (tableType === 'groups') {
          return groupsColumns?.includes(item.key);
        }

        return audiosColumns?.includes(item.key);
      })
      .map((item) => item.key);
  }, [columns, audiosColumns, groupsColumns]);

  const handleClick = useCallback(
    (key: string) => (tableType === 'groups' ? setGroupsColumn(key) : setAudiosColumn(key)),
    [filteredColumns, tableType]
  );

  return (
    <ColumnsFiltersWrapper>
      <ColumnsFiltersButton onClick={() => setIsExpanded(!isExpanded)} className='group'>
        <TableIcon tw='stroke-text-and-icon-muted' />
      </ColumnsFiltersButton>

      {isExpanded && (
        <ColumnsFiltersContainer ref={dropdownRef}>
          {columns.map((column) => (
            <ColumnFilterItem onClick={() => handleClick(column.key)} key={column.key}>
              <span>{t(`insights.my_library_page.table.${column.key}`)}</span>
              {filteredColumns.includes(column.key) && <CheckMarkIcon tw='stroke-accent-1' />}
            </ColumnFilterItem>
          ))}
          <div tw='h-[1px] w-full bg-gray-1' />
          <ColumnFilterItem tw='!justify-start gap-[8px] !text-text-and-icon-muted'>
            <Switch
              id='library-table-data-switch'
              name='library-table-data-switch'
              onChange={(val) => setColorBlindMode(val)}
              isActive={colorBlindMode}
            />
            <label htmlFor='library-table-data-switch'>Color Blind Help</label>
          </ColumnFilterItem>
        </ColumnsFiltersContainer>
      )}
    </ColumnsFiltersWrapper>
  );
};
